import type {
  CostBenefitSnapshot,
  GoalSnapshotResponse,
  ProductCostsData,
  ProductCostsSource,
  ProductDetailsResponse,
  SnapshotInvalidation,
} from "@repo/shared/types";
import { get, post } from "../_client";

const BASE = "/internal/flow-instances";

export function getPortfolioPortfolioSnapshot(portfolioId: string) {
  return get<GoalSnapshotResponse>(`${BASE}/portfolios/${portfolioId}/snapshot/portfolio`);
}

export function getPortfolioXraySnapshot(portfolioId: string) {
  return get<GoalSnapshotResponse>(`${BASE}/portfolios/${portfolioId}/snapshot/xray`);
}

export function getPortfolioAssetAllocationSnapshot(portfolioId: string) {
  return get<GoalSnapshotResponse>(`${BASE}/portfolios/${portfolioId}/snapshot/asset-allocation`);
}

export function getPortfolioHistoricalReturnSnapshot(portfolioId: string) {
  return get<GoalSnapshotResponse>(`${BASE}/portfolios/${portfolioId}/snapshot/historical-return`);
}

export function getPortfolioForecastSnapshot(portfolioId: string) {
  return get<GoalSnapshotResponse>(`${BASE}/portfolios/${portfolioId}/snapshot/forecast`);
}

export function getPortfolioCostSnapshot(portfolioId: string) {
  return get<GoalSnapshotResponse>(`${BASE}/portfolios/${portfolioId}/snapshot/cost`);
}

export function getPortfolioExpectedValueSnapshot(portfolioId: string) {
  return get<GoalSnapshotResponse>(`${BASE}/portfolios/${portfolioId}/snapshot/expected-value`);
}

/**
 * Product details for the portfolio-display info modal on the ADVISOR surface.
 * Language comes from the advisor's session (`user.language`) server-side —
 * the endpoint takes no `lang` query param. Public mirror:
 * `getPublicProductDetails` in `@/lib/api/public-flow/product-details`.
 */
export function getProductDetails(productId: string) {
  return get<ProductDetailsResponse>(`${BASE}/products/${productId}/details`);
}

/**
 * The same modal inside a flow: the portfolio names the instance whose advisor
 * cost overrides (QL-20) the costs show.
 */
export function getPortfolioProductDetails(portfolioId: string, productId: string) {
  return get<ProductDetailsResponse>(
    `${BASE}/portfolios/${portfolioId}/products/${productId}/details`,
  );
}

// QP-177 — existing portfolio comparison snapshots (followup sessions)
/** QL-416 — cost/benefit comparison; the existing side is embedded in the payload. */
export function getPortfolioCostBenefitSnapshot(portfolioId: string) {
  return get<GoalSnapshotResponse<CostBenefitSnapshot>>(
    `${BASE}/portfolios/${portfolioId}/snapshot/cost-benefit`,
  );
}

export function getExistingXraySnapshot(portfolioId: string) {
  return get<GoalSnapshotResponse>(`${BASE}/portfolios/${portfolioId}/snapshot/existing-xray`);
}

export function getExistingAssetAllocationSnapshot(portfolioId: string) {
  return get<GoalSnapshotResponse>(
    `${BASE}/portfolios/${portfolioId}/snapshot/existing-asset-allocation`,
  );
}

export function regeneratePortfolioSnapshots(portfolioId: string) {
  return post<{ success: boolean; refreshHints?: SnapshotInvalidation[] }>(
    `${BASE}/portfolios/${portfolioId}/generate-snapshots`,
    {},
  );
}

// QL-417 — product costs of one portfolio. "existing" lists the frozen holdings
// of a followup portfolio; the server falls back to "new" otherwise and says so
// in `source`.
export function getPortfolioProductCosts(
  portfolioId: string,
  source: ProductCostsSource,
  lang: string,
) {
  const params = new URLSearchParams({ source, lang });
  return get<ProductCostsData>(`${BASE}/portfolios/${portfolioId}/product-costs?${params}`);
}
